/**
 * Date and time in the widget bar (#1822): wide, small digital or small analog.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { WidgetProps } from '@/types/widget'
import { WIDGET_TILE_CLASS } from './tile'

export function ClockBarWidget({ config }: WidgetProps) {
  const { t, i18n } = useTranslation()
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [])

  const locale = i18n.language.startsWith('de') ? 'de-DE' : 'en-GB'
  const time = now.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
  const isSmall = config.size === 'small'
  const date = now.toLocaleDateString(
    locale,
    isSmall
      ? { weekday: 'short', day: 'numeric', month: 'short' }
      : { weekday: 'long', day: 'numeric', month: 'long' },
  )

  if (isSmall && config.settings?.clockStyle === 'analog') {
    const seconds = now.getSeconds()
    const minutes = now.getMinutes() + seconds / 60
    const hours = (now.getHours() % 12) + minutes / 60
    return (
      <section
        aria-label={t('widgetBar.names.clock')}
        className={`${WIDGET_TILE_CLASS} flex-col items-center justify-center gap-1.5 p-2.5`}
      >
        <svg width="100" height="100" viewBox="0 0 100 100" role="img" aria-label={time}>
          <circle cx="50" cy="50" r="47" fill="none" stroke="var(--window-border-color)" strokeWidth="1.5" />
          <path d="M50 8v7M92 50h-7M50 92v-7M8 50h7" stroke="var(--text-secondary)" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="50" y1="50" x2="50" y2="27" stroke="var(--text-color)" strokeWidth="4" strokeLinecap="round" transform={`rotate(${hours * 30} 50 50)`} />
          <line x1="50" y1="50" x2="50" y2="15" stroke="var(--text-color)" strokeWidth="3" strokeLinecap="round" transform={`rotate(${minutes * 6} 50 50)`} />
          <line x1="50" y1="56" x2="50" y2="12" stroke="var(--theme-interactive-color)" strokeWidth="1.5" strokeLinecap="round" transform={`rotate(${seconds * 6} 50 50)`} />
          <circle cx="50" cy="50" r="3.5" fill="var(--theme-interactive-color)" />
        </svg>
        <div className="text-xs text-[var(--text-secondary)]">{date}</div>
      </section>
    )
  }

  return (
    <section
      aria-label={t('widgetBar.names.clock')}
      className={`${WIDGET_TILE_CLASS} flex-col justify-center ${isSmall ? 'gap-2.5 p-3.5' : 'gap-1.5 px-[18px] py-3.5'}`}
    >
      <div className={`font-black leading-none tabular-nums text-[var(--text-color)] ${isSmall ? 'text-[44px]' : 'text-5xl'}`}>
        {time}
      </div>
      <div className={`text-[var(--text-secondary)] ${isSmall ? 'text-[13px]' : 'text-sm'}`}>{date}</div>
    </section>
  )
}
